import React, { useEffect, useRef, useState } from "react";
import { AvatarChip } from "./AvatarChip";
import { fetchTeamMessages, sendTeamMessage, API } from "../lib/api";

/**
 * TeamChat — members-only chat panel embedded in the Team modal.
 *
 * Uses a lightweight HTTP polling fallback if the WebSocket fails. Echoes
 * the LeagueChat layout so the visual language stays consistent.
 */
export function TeamChat({ teamId, user, onToast }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [wsStatus, setWsStatus] = useState("connecting");   // connecting | open | offline
  const wsRef = useRef(null);
  const endRef = useRef(null);
  const seenRef = useRef(new Set());

  const pushMessage = (m) => {
    if (!m || !m.message_id) return;
    if (seenRef.current.has(m.message_id)) return;
    seenRef.current.add(m.message_id);
    setMessages((prev) => [...prev, m].slice(-200));
  };

  // Initial load + polling fallback when WS is offline.
  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;
    const load = async () => {
      try {
        const rows = await fetchTeamMessages(teamId);
        if (cancelled) return;
        seenRef.current = new Set();
        rows.forEach((m) => seenRef.current.add(m.message_id));
        setMessages(rows);
      } catch (e) {
        onToast?.(e?.response?.data?.detail || "Couldn't load chat.");
      }
    };
    load();
    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [teamId]);   // eslint-disable-line react-hooks/exhaustive-deps

  // WebSocket live updates with auto-reconnect (exponential backoff).
  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    let retryDelay = 1000;
    let retryTimer = null;

    const connect = () => {
      if (cancelled) return;
      const url = `${API.replace(/^http/, "ws")}/ws/teams/${teamId}`;
      let ws;
      try {
        ws = new WebSocket(url);
      } catch {
        setWsStatus("offline");
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;
      setWsStatus("connecting");
      ws.onopen = () => { retryDelay = 1000; setWsStatus("open"); };
      ws.onclose = () => { setWsStatus("offline"); scheduleReconnect(); };
      ws.onerror = () => { setWsStatus("offline"); };
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "message" && data.message) pushMessage(data.message);
        } catch { /* ignore */ }
      };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => {
        retryDelay = Math.min(retryDelay * 2, 15000);
        connect();
      }, retryDelay);
    };

    connect();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      try { wsRef.current?.close(); } catch { /* ignore */ }
    };
  }, [teamId]);

  // Auto-scroll on new messages.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const submit = async (e) => {
    e.preventDefault();
    const txt = body.trim();
    if (!txt || busy) return;
    setBusy(true);
    try {
      if (wsRef.current && wsRef.current.readyState === 1) {
        wsRef.current.send(JSON.stringify({ body: txt }));
        setBody("");
      } else {
        const msg = await sendTeamMessage(teamId, txt);
        pushMessage(msg);
        setBody("");
      }
    } catch (e2) {
      onToast?.(e2?.response?.data?.detail || "Send failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="team-hq-chat"
      className="flex flex-col rounded-2xl border border-[#14b8a6]/30 bg-black/40 overflow-hidden"
      style={{ height: "min(520px, 65vh)" }}
    >
      <div className="px-3 py-2 flex items-center justify-between border-b border-[#14b8a6]/15 bg-black/40">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-message-text mr-1" />TEAM CHAT
        </p>
        <span
          data-testid="team-hq-chat-ws-status"
          className={`text-[9px] tracking-widest px-2 py-0.5 rounded-md border ${
            wsStatus === "open"
              ? "border-[#14b8a6]/40 text-[#14b8a6] bg-[#14b8a6]/10"
              : "border-[#14b8a6]/40 text-[#14b8a6] bg-[#14b8a6]/10"
          }`}
        >
          {wsStatus === "open" ? "● LIVE" : "○ OFFLINE"}
        </span>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2" data-testid="team-hq-chat-list">
        {messages.length === 0 ? (
          <p className="text-[12px] text-white/50 text-center mt-6" data-testid="team-hq-chat-empty">
            No messages yet — say hi to your team.
          </p>
        ) : messages.map((m) => {
          const mine = m.user_id === user?.user_id;
          return (
            <div
              key={m.message_id}
              data-testid={`team-hq-chat-msg-${m.message_id}`}
              className={`flex items-start gap-2 ${mine ? "justify-end" : "justify-start"}`}
            >
              {!mine && <AvatarChip avatarId={m.user_avatar_id} pfpUrl={m.user_pfp_url} size={28} />}
              <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-[12px] leading-snug ${
                mine
                  ? "bg-[#14b8a6]/20 border border-[#14b8a6]/40 text-white"
                  : "bg-black/50 border border-white/10 text-white/90"
              }`}>
                {!mine && (
                  <p className="text-[10px] font-bold tracking-wide text-[#a3a3ac] flex items-center gap-1">
                    {m.user_name}
                    {m.role === "owner" && <i className="fas fa-crown text-[#14b8a6] text-[8px]" title="Owner" />}
                    {m.role === "captain" && <i className="fas fa-star text-[#14b8a6] text-[8px]" title="Captain" />}
                  </p>
                )}
                <p className="whitespace-pre-wrap break-words">{m.body}</p>
              </div>
              {mine && <AvatarChip avatarId={user?.avatar_id} pfpUrl={user?.pfp_url} size={28} />}
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={submit} className="shrink-0 flex gap-2 p-2 border-t border-[#14b8a6]/15 bg-black/50">
        <input
          type="text"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={1000}
          placeholder="Type a message to your team…"
          data-testid="team-hq-chat-input"
          className="flex-1 bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm"
        />
        <button
          type="submit"
          disabled={busy || !body.trim()}
          data-testid="team-hq-chat-send"
          className="text-[11px] tracking-widest px-4 rounded-xl btn-neon disabled:opacity-60"
        >
          {busy ? "..." : "SEND"}
        </button>
      </form>
    </div>
  );
}

export default TeamChat;
